import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowRight, Loader2, GraduationCap, Users, UserCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import PasswordStrengthMeter, { isPasswordValid } from './PasswordStrengthMeter';

const ROLE_OPTIONS = [
  { value: 'guru', label: 'Guru / Pendidik', icon: GraduationCap, description: 'Saya seorang guru atau pendidik' },
  { value: 'ortu', label: 'Orang Tua', icon: Users, description: 'Saya orang tua yang ingin belajar' },
  { value: 'umum', label: 'Umum', icon: UserCircle, description: 'Saya ingin belajar mandiri' },
];

const AuthModal = ({ isOpen, onClose, mode, onSwitchMode }) => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'umum',
  });

  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (mode === 'register' && !isPasswordValid(formData.password)) {
      toast.error('Password tidak memenuhi persyaratan keamanan');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'login') {
        const response = await login(formData.email, formData.password);
        if (response?.email_verified === false) {
          toast.info('Login berhasil! Silakan verifikasi email Anda untuk akses penuh.');
        } else {
          toast.success('Login berhasil! Selamat datang kembali.');
        }
        onClose();
        navigate('/mulai');
      } else {
        await register(formData.name, formData.email, formData.password, formData.role);
        toast.success('Registrasi berhasil! Silakan cek email Anda untuk verifikasi.');
        onClose();
        navigate('/mulai');
      }
      setFormData({ name: '', email: '', password: '', role: 'umum' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Terjadi kesalahan';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    onClose();
    navigate('/forgot-password');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden border-0">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white">
              {mode === 'login' ? 'Selamat Datang!' : 'Bergabung di Akademia Fitrah'}
            </DialogTitle>
            <p className="text-sm mt-2 text-blue-100">
              {mode === 'login'
                ? 'Masuk untuk melanjutkan perjalanan belajar'
                : 'Pilih peran Anda dan mulai perjalanan kesadaran'}
            </p>
          </DialogHeader>
        </div>

        {/* Form */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-gray-700">Nama Lengkap</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Masukkan nama lengkap"
                      className="pl-10 py-5 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Role Selection */}
                <div className="space-y-2">
                  <Label className="text-gray-700">Saya mendaftar sebagai</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {ROLE_OPTIONS.map((option) => {
                      const Icon = option.icon;
                      const isSelected = formData.role === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, role: option.value })}
                          className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all text-center ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50 text-blue-700'
                              : 'border-gray-200 hover:border-gray-300 text-gray-600'
                          }`}
                        >
                          <Icon className={`w-6 h-6 mb-1 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                          <span className="text-xs font-medium">{option.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-gray-700">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Masukkan email"
                  className="pl-10 py-5 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-gray-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Masukkan password"
                  className="pl-10 pr-10 py-5 rounded-xl border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  minLength={mode === 'register' ? 8 : 1}
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {mode === 'register' && <PasswordStrengthMeter password={formData.password} />}
            </div>

            {mode === 'login' && (
              <div className="flex justify-end">
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:underline"
                  onClick={handleForgotPassword}
                >
                  Lupa password?
                </button>
              </div>
            )}

            <Button
              type="submit"
              className="w-full font-semibold py-5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white"
              disabled={loading || (mode === 'register' && !isPasswordValid(formData.password))}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Memproses...
                </>
              ) : (
                <>
                  {mode === 'login' ? 'Masuk' : 'Daftar'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>

            {/* Switch Mode */}
            <p className="text-center text-gray-600 text-sm mt-6">
              {mode === 'login' ? 'Belum punya akun?' : 'Sudah punya akun?'}{' '}
              <button
                type="button"
                className="text-blue-600 font-semibold hover:underline"
                onClick={onSwitchMode}
              >
                {mode === 'login' ? 'Daftar sekarang' : 'Masuk'}
              </button>
            </p>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
